import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import { usePendingOAuth } from '../contexts/PendingOAuthContext.jsx';
import { api } from '../services/api/client.js';
import { missingBrandFields } from '../lib/activationProgress.js';
import { effectiveAccessState } from '../lib/billingState.js';
import { isPostingPlan } from '../lib/finishSetup.js';
import { NEW_PLAN_KEY_BY_ID, PLAN_ID_BY_KEY } from '../lib/plans.js';

const unwrap = value => value?.data ?? value;
const hasPending = value => Boolean(value?.pending || value?.personalPendingOptions?.length || value?.connectionTransitionActive);

function realTrial(entitlements) {
  const plan = entitlements?.plan || entitlements?.planKey;
  return entitlements?.freeTrialOnboardingEligible === true
    && effectiveAccessState(entitlements) === 'trialing'
    && (entitlements.billingStatus || entitlements.subscriptionStatus) === 'trialing'
    && entitlements.cancelAtPeriodEnd === false
    && typeof entitlements.stripeSubscriptionId === 'string' && entitlements.stripeSubscriptionId.startsWith('sub_')
    && (Object.hasOwn(NEW_PLAN_KEY_BY_ID, plan) || Object.hasOwn(PLAN_ID_BY_KEY, plan))
    && !isPostingPlan(entitlements);
}

// One entry point for hosted checkout, the preview wall's retained return and
// an ordinary /app visit. A query string is never proof of trial access.
// Keep routed children and PreviewResumeHost unmounted until the decision, so
// neither a welcome overlay nor a resume bar consumes the return first.
export default function PostTrialOnboardingGate({ children, resume = null, fallback = null }) {
  const { user } = useAuth();
  const pendingOAuth = usePendingOAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const userId = user?.id;
  const organizationId = user?.organizationId;
  const owner = JSON.stringify([userId, organizationId]);
  const current = useRef();
  current.current = { user, owner, pendingOAuth };
  const [settled, setSettled] = useState(null);

  useEffect(() => {
    let stopped = false;
    const readOwner = api.getReadOwnerScope?.();
    const sameIdentity = () => !stopped && current.current.owner === owner;
    const sameOwner = () => sameIdentity()
      && api.getReadOwnerScope?.() === readOwner;
    const allow = (resumeAllowed = false) => { if (sameIdentity()) setSettled({ owner, resumeAllowed }); };
    const params = new URLSearchParams(location.search);
    if (!userId || !organizationId || readOwner == null || user?.bypassOnboarding === true
      || params.get('checkout') === 'cancelled'
      || ['provider', 'connected', 'error'].some(key => params.has(key))) {
      allow();
      return () => { stopped = true; };
    }
    if (hasPending(pendingOAuth)) {
      pendingOAuth.releaseCheckoutChoice?.();
      allow();
      return () => { stopped = true; };
    }
    // A failed/stalled optional setup check must not trap someone in the app.
    // Late results after this fallback can no longer redirect them.
    const timeout = setTimeout(() => { allow(); stopped = true; }, 4000);
    (async () => {
      let resumeAllowed = false;
      try {
        const entitlements = unwrap(await api.billing.getEntitlements({ fresh: true }));
        if (!sameOwner() || !['trialing', 'active'].includes(effectiveAccessState(entitlements))) return;
        const key = realTrial(entitlements)
          ? `dathent.trialSetup.${JSON.stringify([userId, organizationId, entitlements.stripeSubscriptionId])}` : null;
        const storage = window.localStorage;
        const needsCheck = key && !storage.getItem(key);
        const [brandResponse, chatgptResponse, claudeResponse] = await Promise.all([
          needsCheck ? api.profile.getBrandProfile({ fresh: true }) : Promise.resolve(null),
          api.auth.getPendingOAuth(),
          api.auth.getPendingOAuth({ connector: 'claude' }),
        ]);
        if (!sameOwner() || current.current.user?.bypassOnboarding === true) return;
        const chatgpt = unwrap(chatgptResponse);
        const claude = unwrap(claudeResponse);
        if (hasPending(current.current.pendingOAuth) || chatgpt?.pending === true || claude?.pending === true) {
          current.current.pendingOAuth.releaseCheckoutChoice?.();
          return;
        }
        // Missing/failed host discovery is unknown, not evidence of no return.
        if (chatgpt?.pending !== false || claude?.pending !== false) return;
        resumeAllowed = true;
        if (!needsCheck) return;
        const data = unwrap(brandResponse);
        const profile = data?.brandProfile || data;
        if (!profile?.id || Array.isArray(profile) || !missingBrandFields(profile).length) return;
        if (storage.getItem(key)) return;
        // Persist BEFORE navigating. Skip, refresh and Back must be terminal
        // for this owner/subscription; blocked storage means no automatic jump.
        storage.setItem(key, 'shown');
        resumeAllowed = false;
        current.current.pendingOAuth.releaseCheckoutChoice?.();
        navigate('/onboarding?source=trial-setup', { replace: true });
      } catch { /* unknown access/profile/storage: keep the existing app usable */ }
      finally {
        clearTimeout(timeout);
        allow(resumeAllowed);
      }
    })();
    return () => { stopped = true; clearTimeout(timeout); };
    // Once per authenticated layout entry, not every screen or cache refresh.
    // Pending OAuth changes are checked from the live ref before navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, organizationId]);

  if (settled?.owner !== owner) return fallback;
  return <>{children}{settled.resumeAllowed && !hasPending(pendingOAuth) ? resume : null}</>;
}
